// @LTG-ID: data-archetypes
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: 04b_SHARED_CONTRACT.md, 08_FEATURES.md#F-007, source_reference_v3_pending/Landing Page.html (v3) lines 1819-1840
// @CHANGED: 2026-05-24 session_005 (CHG-0076, ORCH-0073) — v3 reconciliation: VALUE_GAP_BY_SECTOR top values raised on 10 of 12 rows; default + other unchanged
// @OWNER: web
//
// OwnerArchetype quiz + result definitions + per-sector Value Gap data.
// VALUE_GAP_BY_SECTOR was ported from v2 Landing Page.html lines 1819-1831;
// v3 raised 10 of 12 top values to mirror the INDUSTRY_MULTIPLES upper-bound
// audit documented in source_reference_v3_pending/README.md §1.10.a.

export const ARCHETYPE_QUESTIONS = [
    {
        q: "What's actually pulling you to think about your business value?",
        opts: [
            {
                text: "I built a great life around this business. No real plan to exit.",
                w: { lifestyle: 3, legacy: 1, liquidity: 0, stuck: 1 },
            },
            {
                text: "I want what I built to outlive me, ideally in the family or with the team.",
                w: { lifestyle: 1, legacy: 3, liquidity: 0, stuck: 0 },
            },
            {
                text: "I have a real exit date in mind. I want maximum proceeds when I go.",
                w: { lifestyle: 0, legacy: 1, liquidity: 3, stuck: 0 },
            },
            {
                text: "Honestly, I'd leave tomorrow, but the business can't run without me.",
                w: { lifestyle: 0, legacy: 0, liquidity: 1, stuck: 3 },
            },
        ],
    },
    {
        q: "Right now, the business...",
        opts: [
            {
                text: "Funds the lifestyle I want and I'm not chasing more.",
                w: { lifestyle: 3, legacy: 0, liquidity: 0, stuck: 0 },
            },
            {
                text: "Could be bigger if I focused on it. I'm choosing not to.",
                w: { lifestyle: 2, legacy: 2, liquidity: 0, stuck: 0 },
            },
            {
                text: "Is growing, and I want to keep that going through the next chapter.",
                w: { lifestyle: 0, legacy: 2, liquidity: 2, stuck: 0 },
            },
            {
                text: "Runs me more than I run it.",
                w: { lifestyle: 0, legacy: 0, liquidity: 0, stuck: 3 },
            },
        ],
    },
    {
        q: "If you stopped working tomorrow, how long would the business run on its own?",
        opts: [
            {
                text: "Forever. The team handles it.",
                w: { lifestyle: 1, legacy: 3, liquidity: 2, stuck: 0 },
            },
            {
                text: "Maybe six months before things slow down.",
                w: { lifestyle: 2, legacy: 2, liquidity: 1, stuck: 0 },
            },
            {
                text: "A month, tops.",
                w: { lifestyle: 1, legacy: 0, liquidity: 1, stuck: 2 },
            },
            {
                text: "Less than a week.",
                w: { lifestyle: 0, legacy: 0, liquidity: 0, stuck: 3 },
            },
        ],
    },
    {
        q: "What would change for you if you knew today, with a number, what your business is actually worth?",
        opts: [
            {
                text: "I'd sleep better. I want validation of what I've built.",
                w: { lifestyle: 3, legacy: 1, liquidity: 0, stuck: 0 },
            },
            {
                text: "I'd start the conversation with family or partners about transition.",
                w: { lifestyle: 0, legacy: 3, liquidity: 1, stuck: 0 },
            },
            {
                text: "I'd plan the next 24 months around the gap to my number.",
                w: { lifestyle: 0, legacy: 1, liquidity: 3, stuck: 0 },
            },
            {
                text: "I'd know how far I am from a business I could actually leave.",
                w: { lifestyle: 0, legacy: 0, liquidity: 1, stuck: 3 },
            },
        ],
    },
];

export const ARCHETYPES = {
    lifestyle: {
        label: "The Lifestyle Operator",
        headline:
            "You built a great life around this business. Now you want to know what you've built.",
        body: "Your business funds the life you want. That's already a win. The reason to know your number is not pressure, it's clarity. The Strategic Snapshot validates what you've created and tells you, in writing, what you'd take home if circumstances ever changed. Most owners in your archetype use it as proof of work, not as an exit catalyst.",
        cta: "See your number, signed by our CPA team",
        color: "#18B0BD",
    },
    legacy: {
        label: "The Legacy Builder",
        headline:
            "You're building something to outlive you. The number is how you start the family conversation.",
        body: "You've been quietly hoping the business continues after you, in the family, with management, or with an aligned strategic. The Strategic Snapshot gives you the defensible number every legacy plan starts with: what's it worth to transfer, what's it worth to keep, and what the gap looks like over the next 36 to 60 months. The earlier you have this number, the more flexibility you have to design the transition you actually want.",
        cta: "Start the legacy plan with a number",
        color: "#B08D3E",
    },
    liquidity: {
        label: "The Liquidity-Bound Owner",
        headline:
            "You have a date in mind. You need to know the gap between today's number and what you actually need.",
        body: "You're not casually curious; you're running a timeline. The Strategic Snapshot tells you your indicative valuation range, the three highest-impact moves that close your gap, and the tax exposure that will eat into your headline price. Owners in your archetype use the Snapshot as the operating document for the 24 months before a sale. Order one, work it for 12 to 24 months, then reorder before going to market.",
        cta: "Build your exit timeline around a number",
        color: "#0A2A5E",
    },
    stuck: {
        label: "The Stuck Owner",
        headline:
            "You'd leave if you could. The Snapshot tells you how far you actually are from being able to.",
        body: "The business runs you. That means today, you do not have a business you can sell, you have a job with revenue. That's not a verdict, it's a starting point. The Strategic Snapshot scores your operation across the ten levers that decide whether a business is transferable, gives you the three priority moves to break dependency, and shows what a year of focused work can move on the multiple. Most stuck owners are 18 to 24 months from a meaningfully better number, not five years.",
        cta: "Score where you actually stand",
        color: "#3a4a63",
    },
};

export const VALUE_GAP_BY_SECTOR = {
    default:       { ownerGuess: 4.5, marketActual: 3.2, top: 5.4,  basis: "revenue multiple" },
    trades:        { ownerGuess: 6.0, marketActual: 4.0, top: 10.0, basis: "EBITDA multiple" },
    restaurants:   { ownerGuess: 4.2, marketActual: 2.8, top: 6.0,  basis: "EBITDA multiple" },
    retail:        { ownerGuess: 3.0, marketActual: 2.1, top: 3.6,  basis: "EBITDA multiple" },
    auto:          { ownerGuess: 5.0, marketActual: 3.2, top: 6.5,  basis: "EBITDA multiple" },
    healthcare:    { ownerGuess: 9.5, marketActual: 6.5, top: 13.0, basis: "EBITDA multiple" },
    professional:  { ownerGuess: 5.5, marketActual: 3.5, top: 7.5,  basis: "EBITDA multiple" },
    manufacturing: { ownerGuess: 7.5, marketActual: 5.0, top: 10.0, basis: "EBITDA multiple" },
    personal:      { ownerGuess: 3.5, marketActual: 2.4, top: 4.5,  basis: "EBITDA multiple" },
    childcare:     { ownerGuess: 4.5, marketActual: 3.0, top: 6.0,  basis: "EBITDA multiple" },
    technology:    { ownerGuess: 7.5, marketActual: 5.0, top: 9.0,  basis: "EBITDA multiple" },
    other:         { ownerGuess: 4.5, marketActual: 3.2, top: 5.4,  basis: "EBITDA multiple" },
};
